import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  clearHandoffAttempts,
  readHandoffFromFragment,
  redirectToIlLogin,
} from '../config/ilHandoff';
import { ilClinicUiUrl } from '../config/appMode';

/**
 * Where an Israeli sign-in lands.
 *
 * Israel signs a 60-second assertion and puts it in this URL's fragment; this
 * page exchanges it for an ordinary UK session and continues to whatever page
 * the person originally asked for. On the way through it shows nothing but a
 * line of text, because in the normal case it is on screen for a moment.
 *
 * It is a PUBLIC route - mounted outside the authenticated tree - since its
 * whole job is to run before there is a session.
 *
 * On failure it never shows a password form (there is no password to type) and
 * never bounces on its own past the attempt cap: an account Israel is happy
 * with but this system has never been told about would otherwise be thrown
 * between the two for ever.
 */
export default function IlAuthLandingPage() {
  const { authenticateIlHandoff, commitSession } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  // StrictMode mounts effects twice in development, and this assertion is
  // single-use: the second exchange would be refused as a replay and show an
  // error over a session that is actually fine.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const { token, returnTo } = readHandoffFromFragment();
    if (!token) {
      // Someone opened /il-auth by hand, or the fragment was stripped on the
      // way here. Start the sign-in rather than explaining the mechanism.
      if (!redirectToIlLogin(returnTo)) {
        setError('Could not sign in through the Israeli system.');
      }
      return;
    }

    authenticateIlHandoff(token)
      .then((user) => {
        clearHandoffAttempts();
        commitSession(user);
        navigate(returnTo, { replace: true });
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        setError(
          typeof detail === 'string'
            ? detail
            : 'This sign-in link could not be used. Please try again from the Israeli system.',
        );
      });
  }, [authenticateIlHandoff, commitSession, navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6">
        <div className="w-full max-w-md rounded-lg bg-white p-6 shadow">
          <h1 className="mb-2 text-lg font-semibold text-gray-900">
            Could not sign in
          </h1>
          <p className="mb-4 text-sm text-gray-600">{error}</p>
          <a
            href={ilClinicUiUrl()}
            className="inline-block rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Back to the Israeli system
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <p className="text-sm text-gray-500">Signing you in…</p>
    </div>
  );
}
